import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";
import Modal from "./Modal";
import Button from "./Button";

interface ConfirmDialogProps {
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmLabel?: string;
    onConfirm: () => Promise<void> | void;
    onClose: () => void;
}

/** "Are you sure?" dialog. Shows a spinner while onConfirm runs. */
export default function ConfirmDialog({ open, title, message, confirmLabel = "Delete", onConfirm, onClose }: ConfirmDialogProps) {
    const [busy, setBusy] = useState(false);

    const handleConfirm = async () => {
        setBusy(true);
        try {
            await onConfirm();
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={title}
            size="sm"
            locked={busy}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        loading={busy}
                        className="!bg-red-500 hover:!bg-red-600 !shadow-red-500/20"
                    >
                        {confirmLabel}
                    </Button>
                </>
            }
        >
            <div className="flex gap-4">
                <div className="p-3 h-fit rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400">
                    <AlertTriangle size={20} />
                </div>
                <div className="text-sm text-slate-300 leading-relaxed pt-1">{message}</div>
            </div>
        </Modal>
    );
}